import { defineStore } from "pinia"
import {
  CORRECTIVE_WORK_FILTERS_DEFAULT,
  ISSUE_FILTERS_DEFAULT,
  ITEM_FILTERS_DEFAULT,
  REGULAR_WORK_FILTERS_DEFAULT,
  REGULAR_WORK_RAISED_FROM_FILTERS_DEFAULT,
  WORK_ORDER_FILTERS_DEFAULT,
} from "src/util/constants/filters"
import {
  ENTITY_TYPE,
  INCLUDE_REJECTED,
  INCLUDE_SUPERSEDED,
  PART_OF_WORK_ORDER,
  QR_CODE,
  RAISED_THROUGH,
  RFID,
  WORK_TYPE,
} from "src/util/enums/filters"
import { PriorityClass } from "src/util/types/misc"
import { WorkOrderStateOptions, WorkStateOptions } from "src/util/types/components/sfxTable"
import { EntityPages } from "src/util/types/dataModels/entityDefinition"

export type SystemLink = {
  entityType: EntityPages
  entityId: string
  entityDesignation: string
}

export type FilterTimeLine = "latest" | "none" | "any"
export type FilterCalendrical = "days" | "months" | "years"
export type FilterInequality =
  | "less-than"
  | "less-than-equal"
  | "greater-than"
  | "greater-than-equal"

export interface SimpleTemporalFilter {
  timeLine: FilterTimeLine
  operator: FilterInequality
  number?: number
  calendrical: FilterCalendrical
}

export interface RangeTemporalFilter {
  timeLine: FilterTimeLine
  startDate?: string
  startTime?: string
  endDate?: string
  endTime?: string
}

export type IssuesFilter = {
  isRangeTemporal: boolean
  raisedSimple?: SimpleTemporalFilter
  raisedRange?: RangeTemporalFilter
  workDomain?: string[]
  priority?: PriorityClass[]
  state?: string[]
  designation?: string
}

export type RegularWorkFilter = {
  isRangeTemporal: boolean
  performedSimple?: SimpleTemporalFilter
  performedRange?: RangeTemporalFilter
  workDomain?: string[]
  designation?: string
}

export type RegularWorkRaisedFromFilter = {
  isRangeTemporal: boolean
  performedSimple?: SimpleTemporalFilter
  performedRange?: RangeTemporalFilter
  designation?: string
}

export type CorrectiveWorkFilter = {
  isRangeTemporal: boolean
  performedSimple?: SimpleTemporalFilter
  performedRange?: RangeTemporalFilter
  workDomain?: string[]
  designation?: string
}

export type WorkOrderFilter = {
  workType: WORK_TYPE
  isRangeTemporal: boolean
  startDateSimple?: SimpleTemporalFilter
  startDateRange?: RangeTemporalFilter
  priority?: PriorityClass[]
  workDomain?: string[]
  designation?: string
}

export type ItemFilter = {
  itemClass?: string[]
  designation?: string
}

export interface ItemsPageFilters {
  hasFilters: boolean // flag to stop the watcher filtering the data based on default values
  systemLink?: SystemLink
  hasRfid: RFID
  hasQrCode: QR_CODE
  hasIssueFilters: boolean
  hasRegularWorkFilters: boolean
  hasCorrectiveWorkFilters: boolean
  hasWorkOrderFilters: boolean
  issuesFilter: IssuesFilter
  regularWorkFilter: RegularWorkFilter
  correctiveWorkFilter: CorrectiveWorkFilter
  workOrderFilter: WorkOrderFilter
}

export interface IssuesPageFilters {
  hasFilters: boolean
  systemLink?: SystemLink
  workDomain?: string[]
  entityType: ENTITY_TYPE
  raisedThrough: RAISED_THROUGH
  includeSuperseded: INCLUDE_SUPERSEDED
  includeRejected: INCLUDE_REJECTED
  priority?: PriorityClass[]
  state?: string[]
  hasItemFilters: boolean
  hasRegularWorkFilters: boolean
  itemFilter: ItemFilter
  regularWorkFilter: RegularWorkRaisedFromFilter
}

export interface WorkOrderPageFilters {
  hasFilters: boolean
  systemLink?: SystemLink
  workDomain?: string[]
  workType: WORK_TYPE
  entityType: ENTITY_TYPE
  priority?: PriorityClass[]
  state?: WorkOrderStateOptions[]
  assigned: boolean
}

export interface PerformedWorkPageFilters {
  hasFilters: boolean
  systemLink?: SystemLink
  workDomain?: string[]
  entityType: ENTITY_TYPE
  partOfWorkOrder: PART_OF_WORK_ORDER
  workType: WORK_TYPE
  itemClass?: string[]
  areaAttributeSet?: string[]
  state?: WorkStateOptions[]
}

export interface FilterStoreType {
  itemsPageFilters: ItemsPageFilters // Manager mode ItemSelect page filters
  issuesPageFilters: IssuesPageFilters // Manager mode IssueSelect page filters
  workOrderPageFilters: WorkOrderPageFilters // Manager mode WorkOrderSelect page filters
  performedWorkPageFilters: PerformedWorkPageFilters // Manager mode PerformedWorkSelect page filters
}

export const useFilterStore = defineStore("pinia-filterStore", {
  persist: false,

  state: (): FilterStoreType => ({
    itemsPageFilters: {
      hasFilters: false,
      systemLink: undefined,
      hasRfid: RFID.EITHER,
      hasQrCode: QR_CODE.EITHER,
      hasIssueFilters: false,
      hasRegularWorkFilters: false,
      hasCorrectiveWorkFilters: false,
      hasWorkOrderFilters: false,
      issuesFilter: structuredClone(ISSUE_FILTERS_DEFAULT),
      regularWorkFilter: structuredClone(REGULAR_WORK_FILTERS_DEFAULT),
      correctiveWorkFilter: structuredClone(CORRECTIVE_WORK_FILTERS_DEFAULT),
      workOrderFilter: structuredClone(WORK_ORDER_FILTERS_DEFAULT),
    },
    issuesPageFilters: {
      hasFilters: false,
      systemLink: undefined,
      workDomain: undefined,
      entityType: ENTITY_TYPE.EITHER,
      raisedThrough: RAISED_THROUGH.EITHER,
      includeSuperseded: INCLUDE_SUPERSEDED.NO,
      includeRejected: INCLUDE_REJECTED.NO,
      priority: [],
      state: [],
      hasItemFilters: false,
      hasRegularWorkFilters: false,
      itemFilter: structuredClone(ITEM_FILTERS_DEFAULT),
      regularWorkFilter: structuredClone(REGULAR_WORK_RAISED_FROM_FILTERS_DEFAULT),
    },
    workOrderPageFilters: {
      hasFilters: false,
      systemLink: undefined,
      workDomain: undefined,
      workType: WORK_TYPE.EITHER,
      entityType: ENTITY_TYPE.EITHER,
      priority: [],
      state: [],
      assigned: false,
    },
    performedWorkPageFilters: {
      hasFilters: false,
      systemLink: undefined,
      workDomain: undefined,
      entityType: ENTITY_TYPE.EITHER,
      partOfWorkOrder: PART_OF_WORK_ORDER.EITHER,
      workType: WORK_TYPE.EITHER,
      itemClass: undefined,
      areaAttributeSet: undefined,
      state: [],
    },
  }),
})
